import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Plus, Building2, Users, X, Save, ShieldCheck, ShieldOff, Receipt, ChevronDown, UserPlus, ClipboardList, Mail } from "lucide-react";

const CADENCES = ["instant", "weekly", "biweekly", "monthly"];
const fieldCls = "w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm focus:outline-none focus:border-paw-accent";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const REF_STATUS = {
  submitted: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  invited: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  in_treatment: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
  fulfilled: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  cancelled: "bg-black/[0.06] dark:bg-white/10 text-paw-text-muted dark:text-white/50",
};

export default function AdminInsurancePartners() {
  const [tab, setTab] = useState("partners");
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([api.get("/admin/insurance-partners"), api.get("/admin/insurance-referrals")]);
      setPartners(p.data.partners || []);
      setReferrals(r.data.referrals || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const outstanding = partners.reduce((s, p) => s + Number(p.outstanding_balance || 0), 0);

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-5xl" data-testid="admin-insurance-partners">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Insurance</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Partners</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">{partners.length} partner(s) · {referrals.length} referral(s) · {money(outstanding)} outstanding across all partners.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 text-sm transition-colors"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover text-white text-sm font-semibold transition-colors"><Plus className="w-4 h-4" /> New partner</button>
        </div>
      </div>

      <div className="flex gap-1.5 mb-6 border-b border-paw-border dark:border-white/10">
        {[["partners", "Partners", Building2], ["referrals", "Referrals", ClipboardList], ["assign", "Assign member", UserPlus]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-paw-accent text-paw-text dark:text-white" : "border-transparent text-paw-text-muted dark:text-white/50 hover:text-paw-text dark:hover:text-white/80"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-7 h-7 text-paw-text-muted dark:text-white/30 animate-spin" /></div>
      ) : tab === "partners" ? (
        partners.length === 0 ? (
          <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl p-12 text-center">
            <Building2 className="w-9 h-9 text-paw-text-muted dark:text-white/30 mx-auto mb-3" />
            <p className="text-paw-text-secondary dark:text-white/60">No partners yet. Create one to send them portal access.</p>
          </div>
        ) : (
          <div className="space-y-4">{partners.map((p) => <PartnerCard key={p.partner_id} partner={p} onSaved={load} />)}</div>
        )
      ) : tab === "referrals" ? (
        <ReferralsTab referrals={referrals} />
      ) : (
        <AssignTab partners={partners} onDone={load} />
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function PartnerCard({ partner, onSaved }) {
  const [fee, setFee] = useState(partner.med_fee_amount ?? 0);
  const [cadence, setCadence] = useState(partner.settlement_cadence || "monthly");
  const [active, setActive] = useState(partner.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [resending, setResending] = useState(false);
  const dirty = fee !== (partner.med_fee_amount ?? 0) || cadence !== (partner.settlement_cadence || "monthly") || active !== (partner.is_active !== false);

  const resendInvite = async () => {
    setResending(true);
    try {
      await api.post(`/admin/insurance-partners/${partner.partner_id}/resend-invite`);
      toast.success("Invite resent");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResending(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/insurance-partners/${partner.partner_id}`, { med_fee_amount: Number(fee), settlement_cadence: cadence, is_active: active });
      toast.success("Partner updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-lg truncate">{partner.org_name}</h3>
            {active ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Active</span>
                    : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-black/[0.06] dark:bg-white/10 text-paw-text-muted dark:text-white/50">Inactive</span>}
          </div>
          <p className="text-sm text-paw-text-muted dark:text-white/50 mt-1">{partner.contact_name || "n/a"} · {partner.email}</p>
          <p className="text-xs text-paw-text-muted dark:text-white/40 mt-0.5">{partner.phone || ""} {partner.website ? `· ${partner.website}` : ""}</p>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 text-sm text-paw-text-secondary dark:text-white/60 justify-end"><Users className="w-4 h-4" /> {partner.referral_count ?? 0} referrals</div>
          <div className="mt-1 text-sm"><span className="text-paw-text-muted dark:text-white/40">Outstanding </span><span className="font-bold text-amber-700 dark:text-amber-300">{money(partner.outstanding_balance)}</span><span className="text-paw-text-muted dark:text-white/40"> ({partner.outstanding_count || 0})</span></div>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-paw-text-muted dark:text-white/40 mb-1">Agreed medication fee ($)</label>
          <input type="number" min="0" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs text-paw-text-muted dark:text-white/40 mb-1">Settlement cadence</label>
          <select value={cadence} onChange={(e) => setCadence(e.target.value)} className={fieldCls}>
            {CADENCES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <button onClick={() => setActive((a) => !a)} className="flex-1 h-10 rounded-lg border border-paw-border dark:border-white/10 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 text-sm flex items-center justify-center gap-2 transition-colors">
            {active ? <><ShieldOff className="w-4 h-4" /> Deactivate</> : <><ShieldCheck className="w-4 h-4" /> Activate</>}
          </button>
        </div>
      </div>
      {partner.med_fee_note ? <p className="text-xs text-paw-text-muted dark:text-white/40 mt-2">Fee note: {partner.med_fee_note}</p> : null}

      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <button onClick={() => setShowLedger((s) => !s)} className="flex items-center gap-1.5 text-sm text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white transition-colors">
          <Receipt className="w-4 h-4" /> Billing ledger <ChevronDown className={`w-4 h-4 transition-transform ${showLedger ? "rotate-180" : ""}`} />
        </button>
        <div className="flex items-center gap-2">
          <button onClick={resendInvite} disabled={resending} className="flex items-center gap-2 px-4 h-10 rounded-lg border border-paw-border dark:border-white/10 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 disabled:opacity-60 text-sm font-medium transition-colors">
            {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Resend invite
          </button>
          <button onClick={save} disabled={!dirty || saving} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-60 text-white text-sm font-semibold transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>

      {showLedger && <LedgerPanel partner={partner} onSettled={onSaved} />}
    </div>
  );
}

function LedgerPanel({ partner, onSettled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/insurance-partners/${partner.partner_id}/ledger`); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const settle = async () => {
    if (!window.confirm(`Mark all outstanding charges for ${partner.org_name} as settled?`)) return;
    setSettling(true);
    try { const { data } = await api.post(`/admin/insurance-partners/${partner.partner_id}/settle`); toast.success(data.message); await load(); onSettled?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSettling(false); }
  };

  if (loading) return <div className="mt-4 py-6 grid place-items-center"><Loader2 className="w-5 h-5 text-paw-text-muted dark:text-white/30 animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="mt-4 border-t border-paw-border dark:border-white/10 pt-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex gap-5 text-sm">
          <div><span className="text-paw-text-muted dark:text-white/40">Outstanding </span><span className="font-bold text-amber-700 dark:text-amber-300">{money(data.outstanding_balance)}</span></div>
          <div><span className="text-paw-text-muted dark:text-white/40">Settled </span><span className="font-bold text-emerald-700 dark:text-emerald-300">{money(data.settled_total)}</span></div>
          <div className="text-paw-text-muted dark:text-white/40 capitalize">Cadence: {data.partner?.settlement_cadence}</div>
        </div>
        <button onClick={settle} disabled={settling || data.outstanding_count === 0} className="flex items-center gap-2 px-4 h-9 rounded-lg bg-emerald-500/90 hover:bg-emerald-500 disabled:opacity-60 text-[#0e1812] text-sm font-semibold transition-colors">
          {settling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Settle {data.outstanding_count || 0}
        </button>
      </div>
      {data.charges.length === 0 ? (
        <p className="text-sm text-paw-text-muted dark:text-white/40 py-3">No charges yet. Charges accrue when an insurance member's order is confirmed.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto rounded-lg border border-paw-border dark:border-white/10 divide-y divide-paw-border dark:divide-white/5">
          {data.charges.slice(0, 200).map((c) => (
            <div key={c.charge_id} className="px-3 py-2.5 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                <p className="text-paw-text dark:text-white/80 truncate">{c.member_name || c.member_email} · <span className="text-paw-text-muted dark:text-white/50">{c.medication}</span></p>
                <p className="text-xs text-paw-text-muted dark:text-white/40">{c.order_number} · {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-semibold">{money(c.fee_amount)}</div>
                <span className={`text-[11px] font-semibold ${c.status === "settled" ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>{c.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralsTab({ referrals }) {
  const [q, setQ] = useState("");
  const filtered = referrals.filter((r) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [r.member_first_name, r.member_last_name, r.member_email, r.partner_name, r.pet_name].some((v) => (v || "").toLowerCase().includes(t));
  });
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search referrals by member, partner, or pet" className="w-full sm:max-w-md mb-4 h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm focus:outline-none focus:border-paw-accent" />
      {filtered.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl p-12 text-center text-paw-text-muted dark:text-white/50">No referrals.</div>
      ) : (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-paw-text-muted dark:text-white/40 border-b border-paw-border dark:border-white/10">
            <div className="col-span-3">Member</div><div className="col-span-3">Partner</div><div className="col-span-2">Pet</div><div className="col-span-2">Date</div><div className="col-span-2 text-right">Status</div>
          </div>
          <div className="divide-y divide-paw-border dark:divide-white/5">
            {filtered.map((r) => (
              <div key={r.referral_id} className="px-4 py-3 grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-2 sm:items-center text-sm">
                <div className="sm:col-span-3 min-w-0"><p className="font-medium truncate">{r.member_first_name} {r.member_last_name}</p><p className="text-xs text-paw-text-muted dark:text-white/40 truncate">{r.member_email}</p></div>
                <div className="sm:col-span-3 text-paw-text-secondary dark:text-white/70 truncate">{r.partner_name}</div>
                <div className="sm:col-span-2 text-paw-text-secondary dark:text-white/60 truncate">{r.pet_name || "n/a"}</div>
                <div className="sm:col-span-2 text-paw-text-muted dark:text-white/50">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div>
                <div className="sm:col-span-2 sm:text-right"><span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${REF_STATUS[r.status] || REF_STATUS.submitted}`}>{r.status}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AssignTab({ partners, onDone }) {
  const [email, setEmail] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (unassign) => {
    if (!email) { toast.error("Enter the customer's email."); return; }
    if (!unassign && !partnerId) { toast.error("Pick a partner."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/insurance-partners/assign-member", { email: email.trim(), partner_id: unassign ? null : partnerId });
      toast.success(data.message);
      setEmail(""); setPartnerId("");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl p-6 max-w-lg">
      <h3 className="font-bold text-lg mb-1">Assign an existing customer to a partner</h3>
      <p className="text-sm text-paw-text-muted dark:text-white/50 mb-5">Use this when a customer already has a PawLinx account and a partner confirms they're one of their members. This flags them as insurance-covered.</p>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-paw-text-muted dark:text-white/40 mb-1">Customer email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs text-paw-text-muted dark:text-white/40 mb-1">Partner</label>
          <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className={fieldCls}>
            <option value="">Select a partner</option>
            {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.org_name}</option>)}
          </select>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={() => submit(false)} disabled={busy} className="flex items-center gap-2 px-5 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-60 text-white text-sm font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Assign
          </button>
          <button onClick={() => submit(true)} disabled={busy} className="px-4 h-10 rounded-lg border border-paw-border dark:border-white/10 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 text-sm">Revert to regular</button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({ org_name: "", contact_name: "", email: "", phone: "", website: "", address: "", med_fee_amount: 0, med_fee_note: "", settlement_cadence: "monthly", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.org_name || !f.email) { toast.error("Organization name and email are required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/insurance-partners", { ...f, med_fee_amount: Number(f.med_fee_amount) });
      toast.success("Partner created and invited by email");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl w-full max-w-lg my-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-paw-border dark:border-white/10">
          <h2 className="font-bold text-lg">New partner</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/[0.06] dark:hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-3">
          <input className={fieldCls} placeholder="Organization name *" value={f.org_name} onChange={(e) => set("org_name", e.target.value)} />
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={fieldCls} placeholder="Contact name" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
            <input className={fieldCls} placeholder="Portal login email *" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} />
            <input className={fieldCls} placeholder="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            <input className={fieldCls} placeholder="Website" value={f.website} onChange={(e) => set("website", e.target.value)} />
          </div>
          <input className={fieldCls} placeholder="Address" value={f.address} onChange={(e) => set("address", e.target.value)} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-paw-text-muted dark:text-white/40 mb-1">Agreed medication fee ($)</label>
              <input className={fieldCls} type="number" min="0" step="0.01" value={f.med_fee_amount} onChange={(e) => set("med_fee_amount", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-paw-text-muted dark:text-white/40 mb-1">Settlement cadence</label>
              <select className={fieldCls} value={f.settlement_cadence} onChange={(e) => set("settlement_cadence", e.target.value)}>
                {CADENCES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
          </div>
          <input className={fieldCls} placeholder="Fee note (e.g. cost + 10%)" value={f.med_fee_note} onChange={(e) => set("med_fee_note", e.target.value)} />
          <textarea rows={2} className="w-full px-3 py-2 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm focus:outline-none focus:border-paw-accent resize-none" placeholder="Internal notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          <p className="text-xs text-paw-text-muted dark:text-white/40">The contact will receive an email to set their portal password.</p>
        </div>
        <div className="px-6 py-4 border-t border-paw-border dark:border-white/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 h-10 rounded-lg border border-paw-border dark:border-white/10 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-5 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-60 text-white text-sm font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create & invite
          </button>
        </div>
      </form>
    </div>
  );
}
